import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import {
  LayoutDashboard, Phone, PhoneOutgoing, Settings, LogOut, Menu, X,
  ChevronRight, Building2, User, BookUser, Megaphone, UserCheck,
  Calendar, BarChart3, Bell, Activity, ClipboardList, UserCheck as UserCheckIcon,
  PhoneForwarded, BookOpen, Ticket, MessageSquare,
} from 'lucide-react';
import { Button } from '../ui/button';

const EliosSidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [outboundEnabled, setOutboundEnabled] = useState(false);
  const [isCondominio, setIsCondominio] = useState(false);
  const [condominioModuleEnabled, setCondominioModuleEnabled] = useState(false);
  const [whatsappModuleEnabled, setWhatsappModuleEnabled] = useState(false);

  useEffect(() => {
    api.get('/elios-client/profile').then(r => {
      setProfile(r.data);
      setIsCondominio(r.data?.provenienza === 'condominio');
      setCondominioModuleEnabled(!!r.data?.modules?.condominio);
      setWhatsappModuleEnabled(!!r.data?.modules?.whatsapp);
    }).catch(() => {});
    api.get('/elios-client/bootstrap').then(r => {
      setOutboundEnabled(r.data.outbound_enabled || false);
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/elios/login');
  };

  const navItems = isCondominio ? [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/elios/dashboard' },
    { icon: Phone, label: 'Conversazioni', path: '/elios/inbound' },
    ...(whatsappModuleEnabled ? [{ icon: MessageSquare, label: 'WhatsApp', path: '/elios/whatsapp' }] : []),
    ...(condominioModuleEnabled ? [{ icon: Ticket, label: 'Ticketing', path: '/elios/ticketing' }] : []),
    { icon: Calendar, label: 'Calendario', path: '/elios/calendario' },
    { icon: Bell, label: 'Promemoria', path: '/elios/promemoria' },
    { icon: BarChart3, label: 'Analytics', path: '/elios/analytics' },
    { icon: Settings, label: 'Impostazioni', path: '/elios/settings' },
    { icon: BookOpen, label: 'Documentazione', path: '/elios/docs' },
  ] : [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/elios/dashboard' },
    { section: 'Telefonia' },
    { icon: Phone, label: 'Conversazioni', path: '/elios/inbound' },
    ...(outboundEnabled ? [
      { icon: PhoneOutgoing, label: 'Outbound', path: '/elios/outbound' },
    ] : []),
    { icon: PhoneForwarded, label: 'Follow-up', path: '/elios/followup' },
    { icon: ClipboardList, label: 'Assegnate', path: '/elios/assegnate' },
    { section: 'CRM' },
    { icon: UserCheck, label: 'Profilazione', path: '/elios/profili' },
    { icon: BarChart3, label: 'Analisi AI', path: '/elios/analisi-ai' },
    ...(outboundEnabled ? [
      { icon: BookUser, label: 'Rubrica', path: '/elios/rubrica' },
      { icon: Megaphone, label: 'Campagne', path: '/elios/campagne' },
    ] : []),
    { icon: UserCheck, label: 'Leads', path: '/elios/leads' },
    { section: 'Agenda' },
    { icon: Calendar, label: 'Calendario', path: '/elios/calendario' },
    { icon: Bell, label: 'Promemoria', path: '/elios/promemoria' },
    { section: 'Sistema' },
    ...(condominioModuleEnabled ? [{ icon: Ticket, label: 'Ticketing', path: '/elios/ticketing' }] : []),
    ...(whatsappModuleEnabled ? [{ icon: MessageSquare, label: 'WhatsApp', path: '/elios/whatsapp' }] : []),
    { icon: BarChart3, label: 'Analytics', path: '/elios/analytics' },
    { icon: Settings, label: 'Impostazioni', path: '/elios/settings' },
    { icon: BookOpen, label: 'Documentazione', path: '/elios/docs' },
  ];

  return (
    <>
      <button
        data-testid="elios-mobile-menu-btn"
        className="lg:hidden fixed top-3 left-3 z-[60] p-2.5 bg-transparent text-gray-600 active:scale-95 transition-transform"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {isOpen && <div className="lg:hidden fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={() => setIsOpen(false)} />}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-72 lg:w-64 bg-white border-r border-gray-100 shadow-xl lg:shadow-none transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} flex flex-col`}>
        <div className="p-4 pt-6 lg:p-6 border-b border-gray-100 flex justify-center">
          <Link to="/elios/dashboard" className="flex items-center justify-center group" data-testid="elios-logo-link">
            <img
              src="/elios-logo.svg"
              alt="Elios"
              className="h-[4.5rem] w-auto transition-transform duration-300 group-hover:scale-105 elios-logo-pulse"
            />
          </Link>
        </div>

        <div className="p-3 mx-3 mt-2 lg:p-4 lg:mx-4 lg:mt-4 glass rounded-lg" data-testid="elios-sidebar-profile">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
              {(profile?.company_name || '?')[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="font-medium text-sm text-gray-900 leading-tight break-words"
                title={profile?.company_name || ''}
              >
                {profile?.company_name || 'Caricamento...'}
              </p>
              <p className="text-xs text-emerald-600 font-medium truncate">{profile?.referente || ''}</p>
            </div>
          </div>
          <div className="mt-2 pt-1.5 border-t border-gray-200/50">
            <p className="text-[10px] text-gray-400 flex items-center gap-1">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span>Cliente Elios</span>
            </p>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item, idx) => {
              // W8: Section headers (TELEFONIA, CRM, AGENDA, SISTEMA)
              if (item.section) {
                return (
                  <li key={`section-${item.section}`} className="pt-3 pb-1 first:pt-0">
                    <span className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      {item.section}
                    </span>
                  </li>
                );
              }
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path || `item-${idx}`}>
                  <Link
                    to={item.path}
                    data-testid={`elios-nav-${(item.path || '').replace(/\//g, '-').replace(/^-/, '')}`}
                    className={`flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-lg transition-all duration-200 group active:scale-[0.97] ${
                      isActive ? 'bg-emerald-50 text-emerald-600 border-l-2 border-emerald-500 font-medium' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-500' : 'group-hover:text-emerald-500'}`} />
                    <span className="flex-1 text-sm">{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 text-emerald-500" />}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-100 space-y-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-red-500 hover:text-red-600 hover:bg-red-50 active:scale-[0.97]"
            onClick={handleLogout}
            data-testid="elios-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Esci</span>
          </Button>
        </div>
      </aside>
    </>
  );
};

export default EliosSidebar;
